import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { BookmarkIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { KRAJ, jestPolska } from '../../kraj'
import { useAuth } from '../../context/AuthContext'
import { saveKit } from '../../api/account'

const ETYKIETY = {
  cheapest: 'Najtańszy',
  quality: 'Najwyższej jakości',
  best_available: 'Najlepiej dostępny',
  single_seller: 'Jeden sprzedawca',
}

/**
 * Zapis zestawu na konto. Dla niezalogowanych pokazuje zachętę zamiast
 * chować przycisk — inaczej użytkownik nigdy by się nie dowiedział, że konto
 * do czegoś służy. To jest dziś główny powód, żeby je założyć.
 */
const SaveKitButton = ({ variant, requestPayload, nazwaWstepna = '' }) => {
  const { isAuthenticated } = useAuth()
  const { t } = useTranslation('zestaw')
  const [stan, setStan] = useState('idle') // idle | nazwa | busy | done | error
  const [nazwa, setNazwa] = useState('')
  const [error, setError] = useState('')

  if (!isAuthenticated) {
    // Konta (logowanie, rejestracja, maile, panel) są jeszcze tylko po polsku, więc w wersji
  // krajowej ich nie pokazujemy (start Rumunii 2026-10-09).
    if (!jestPolska) return null
    return (
      <p className="mt-3 text-xs text-gray-500">
        <Link to="/logowanie" className="text-blue-600 font-medium hover:underline">{t('zapis.zaloguj')}</Link>
        {t('zapis.zaloguj_ten')}
      </p>
    )
  }

  if (stan === 'done') {
    return (
      <p className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700">
        <CheckIcon className="w-4 h-4" />
        {t('zapis.zapisano')}{' '}
        <Link to="/konto" className="text-blue-600 font-medium hover:underline">{t('zapis.zobacz_zapisane')}</Link>
      </p>
    )
  }

  const zapisz = async () => {
    setStan('busy')
    setError('')
    const res = await saveKit({
      name: nazwa.trim() || domyslnaNazwa(),
      payload: variant,
      request_payload: requestPayload || null,
      total_pln: (variant.totals || {}).total_pln ?? null,
    })
    if (!res.ok) { setError(res.error); setStan('nazwa'); return }
    setStan('done')
  }

  /**
   * Nazwa podstawiana w polu zapisu. Pierwszeństwo ma to, co użytkownik wpisał
   * na GÓRZE asystenta (zwykle nazwisko klienta) — po to tam stoi. Dopiero gdy
   * zostawił puste, składamy nazwę z etykiety wariantu i daty.
   *
   * Przy kilku wariantach naraz sama nazwa klienta nie wystarcza — „Kowalski"
   * zapisany trzy razy to trzy nierozróżnialne pozycje na liście. Dlatego
   * dokładamy etykietę wariantu.
   */
  const domyslnaNazwa = () => {
    const etykieta = ETYKIETY[variant.variant]
      ? t(`zapis.warianty.${variant.variant}`, { defaultValue: ETYKIETY[variant.variant] })
      : t('zapis.zestaw')
    const wlasna = (nazwaWstepna || '').trim()
    if (wlasna) return t('zapis.nazwa_wlasna', { wlasna, etykieta }).slice(0, 120)
    return t('zapis.nazwa_z_data', { etykieta, data: new Date().toLocaleDateString(KRAJ.locale) })
  }

  if (stan === 'idle') {
    return (
      <button
        type="button"
        onClick={() => { setNazwa(domyslnaNazwa()); setStan('nazwa') }}
        className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:underline"
      >
        <BookmarkIcon className="w-4 h-4" />
        {t('zapis.zapisz_ten_zestaw')}
      </button>
    )
  }

  return (
    <div className="mt-3">
      <div className="flex gap-2">
        <input
          type="text"
          value={nazwa}
          onChange={(e) => setNazwa(e.target.value)}
          maxLength={120}
          placeholder={t('zapis.nazwa_zestawu')}
          disabled={stan === 'busy'}
          className="flex-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          type="button"
          onClick={zapisz}
          disabled={stan === 'busy'}
          className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold
                     hover:bg-blue-500 transition-colors disabled:bg-gray-300"
        >
          {stan === 'busy' ? '…' : t('zapis.zapisz')}
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}

export default SaveKitButton
